window.ThemeProduct = window.ThemeProduct || {};

class ThemeProductBundleAddPayloadBuilder {
  static parseVariants(option) {
    if (!option) return [];
    if (Array.isArray(option.variants)) return option.variants;

    const encodedVariants = option.dataset?.bundleVariants || "";
    if (!encodedVariants) return [];

    try {
      const variants = JSON.parse(decodeURIComponent(encodedVariants));
      return Array.isArray(variants) ? variants : [];
    } catch (e) {
      return [];
    }
  }

  static normalizeBundleSelection(row, option, optionIndex = null) {
    if (!row || !option) return null;
    const handle = option.handle || option.dataset?.bundleHandle || "";
    if (!handle) return null;

    const variants = ThemeProductBundleAddPayloadBuilder.parseVariants(option);
    const rowVariantId = Number(row.dataset.bundleVariantId || "") || null;
    const selectedVariant = rowVariantId
      ? variants.find((variant) => Number(variant?.id) === rowVariantId) || null
      : null;
    const variantId = rowVariantId || null;
    const variantAvailable = selectedVariant ? Boolean(selectedVariant.available) : null;
    const variantTitle = String(selectedVariant?.title || "").trim();
    const title = String(option.title || option.dataset?.bundleTitle || handle).trim();
    const image = String(option.preview_image || option.dataset?.bundleImageSrc || "").trim();
    const url = String(option.url || option.dataset?.bundleUrl || "").trim();
    const index =
      Number.isInteger(optionIndex) && optionIndex >= 0
        ? optionIndex
        : Number(row.querySelector(".pdp-connected-grid__cell.is-active")?.dataset?.bundleOptionIndex || 0);

    return {
      handle,
      variantId,
      variantTitle,
      title,
      image,
      url,
      optionIndex: index,
      variantAvailable,
    };
  }

  static getSelectedBundleSelections(formElement, ownerElement = null) {
    const componentRoot = formElement?.closest("product-component") || ownerElement;
    const sectionRoot =
      formElement?.closest(".shopify-section") ||
      componentRoot?.closest?.(".shopify-section") ||
      componentRoot ||
      document;

    const rows = sectionRoot.querySelectorAll(".pdp-bundle-builder__row");
    if (!rows.length) return [];

    const directSelections = Array.from(rows)
      .map((row) => {
        const activeOption = row.querySelector(".pdp-connected-grid__cell.is-active [data-bundle-handle]");
        if (!activeOption) return null;
        return ThemeProductBundleAddPayloadBuilder.normalizeBundleSelection(row, activeOption);
      })
      .filter(Boolean);

    if (directSelections.length === rows.length) return directSelections;

    const variantInput = formElement?.querySelector('input[name="id"]');
    const currentVariantId = variantInput?.value ? String(variantInput.value) : "";
    if (!currentVariantId) return directSelections;

    const cacheNode = sectionRoot.querySelector("[data-bundle-packs-cache]");
    if (!cacheNode) return directSelections;

    let cache = null;
    try {
      cache = JSON.parse(cacheNode.textContent || "{}");
    } catch (e) {
      return directSelections;
    }

    const options = Array.isArray(cache?.variants?.[currentVariantId]?.options)
      ? cache.variants[currentVariantId].options
      : [];
    if (!options.length) return directSelections;

    const fallbackSelections = Array.from(rows)
      .map((row) => {
        const activeCell = row.querySelector(".pdp-connected-grid__cell.is-active");
        if (!activeCell) return null;
        const explicitIndex = Number(activeCell.dataset.bundleOptionIndex);
        const optionIndex = Number.isInteger(explicitIndex) && explicitIndex >= 0
          ? explicitIndex
          : Array.from(activeCell.parentElement?.children || []).indexOf(activeCell);
        const option = options[optionIndex];
        return ThemeProductBundleAddPayloadBuilder.normalizeBundleSelection(row, option, optionIndex);
      })
      .filter(Boolean);

    return fallbackSelections.length ? fallbackSelections : directSelections;
  }

  static async buildCartAddPayload(serialized, formElement, ownerElement = null) {
    const bundleSelections = ThemeProductBundleAddPayloadBuilder.getSelectedBundleSelections(
      formElement,
      ownerElement
    );
    if (!bundleSelections.length) return serialized;

    const componentRoot = formElement?.closest("product-component") || ownerElement;
    const variantSelect = componentRoot?.querySelector?.("variant-selects:not(.upsell-variants)");
    const currentFrameVariantId = variantSelect?.currentVariant?.id || null;
    const frameId = currentFrameVariantId || Number(serialized?.id) || serialized?.id;
    const frameQuantity = Number(serialized?.quantity) || 1;
    if (!frameId) return serialized;
    if (bundleSelections.some((selection) => !selection?.variantId)) {
      throw new Error(window.variantStrings?.selectSize || "Please select a size");
    }
    if (bundleSelections.some((selection) => selection?.variantAvailable === false)) {
      throw new Error(window.variantStrings?.soldOut || "Sold out");
    }

    const existingProperties = serialized?.properties && typeof serialized.properties === "object"
      ? serialized.properties
      : {};
    const properties = {
      ...existingProperties,
    };
    const encodeBundlePropertyPart = (value) => String(value || "").replace(/\|\|/g, " ").trim();

    properties["Bundle contents"] = `${bundleSelections.length} selected ${bundleSelections.length === 1 ? "pack" : "packs"}`;

    for (let index = 0; index < bundleSelections.length; index += 1) {
      const selection = bundleSelections[index] || {};
      const slot = index + 1;
      const title = String(selection.title || selection.handle || `Pack ${slot}`).trim();
      const variantTitleRaw = String(selection.variantTitle || "").trim();
      const variantTitle = variantTitleRaw && variantTitleRaw !== "Default Title" ? variantTitleRaw : "";
      const publicValue = variantTitle ? `${title} - ${variantTitle}` : title;

      properties[`Pack ${slot}`] = publicValue;
    }

    properties._bundle_id = `bndl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    properties._bundle_role = "frame";
    properties._bundle_child_count = String(bundleSelections.length);

    for (let index = 0; index < bundleSelections.length; index += 1) {
      const selection = bundleSelections[index] || {};
      const slot = index + 1;
      const title = String(selection.title || selection.handle || `Pack ${slot}`).trim();
      const variantTitleRaw = String(selection.variantTitle || "").trim();
      const variantTitle = variantTitleRaw && variantTitleRaw !== "Default Title" ? variantTitleRaw : "";

      properties[`_bundle_child_${slot}`] = [
        title,
        variantTitle,
        selection.image,
        selection.url,
      ].map(encodeBundlePropertyPart).join("||");
    }

    const payload = {
      id: frameId,
      quantity: frameQuantity,
      properties,
    };

    if (serialized?.selling_plan) {
      payload.selling_plan = serialized.selling_plan;
    }

    return payload;
  }
}

class ThemeProductBundleBuilderUtils {
  static escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  static sanitizeCssColor(value) {
    const normalized = String(value ?? "").trim();
    if (!normalized) return "";
    return /^[#(),.%\s0-9a-zA-Z-]+$/.test(normalized) ? normalized : "";
  }

  static isDiscoveryBundleMode(bundleRoot) {
    return bundleRoot?.dataset?.bundleRowMode === "discovery";
  }

  static applyDiscoveryRowVariantFromButton(context, row, sourceButton = null) {
    if (!row) return;
    const activeButton =
      sourceButton ||
      row.querySelector('.pdp-connected-grid__cell.is-active [data-action="bundle-select-option"]');
    if (!activeButton) {
      row.dataset.bundleVariantId = "";
      row.dataset.bundleHandle = "";
      return;
    }

    row.dataset.bundleHandle = activeButton.dataset.bundleHandle || "";

    const variants = ThemeProductBundleAddPayloadBuilder.parseVariants(activeButton);
    if (!Array.isArray(variants) || !variants.length) {
      row.dataset.bundleVariantId = "";
      return;
    }

    const selectedVariant = variants.find((variant) => variant?.available) || null;
    const selectedVariantId = Number(selectedVariant?.id || "");
    row.dataset.bundleVariantId = selectedVariantId ? String(selectedVariantId) : "";
  }

  static renderBundleRowSizeOptions(context, row, sourceButton = null) {
    if (!row) return;
    const host = row.querySelector("[data-bundle-size-options]");
    if (!host) return;

    const bundleRoot = row.closest(".pdp-bundle-builder");
    if (ThemeProductBundleBuilderUtils.isDiscoveryBundleMode(bundleRoot)) {
      host.innerHTML = "";
      ThemeProductBundleBuilderUtils.applyDiscoveryRowVariantFromButton(context, row, sourceButton);
      ThemeProductBundleBuilderUtils.updateBundleAddToCartState(context);
      return;
    }

    const activeButton =
      sourceButton ||
      row.querySelector('.pdp-connected-grid__cell.is-active [data-action="bundle-select-option"]');
    if (!activeButton) {
      host.innerHTML = "";
      row.dataset.bundleVariantId = "";
      return;
    }

    const handle = activeButton.dataset.bundleHandle || "";
    row.dataset.bundleHandle = handle;

    const variants = ThemeProductBundleAddPayloadBuilder.parseVariants(activeButton);
    if (!Array.isArray(variants) || !variants.length) {
      host.innerHTML = "";
      row.dataset.bundleVariantId = "";
      return;
    }

    const previousVariantId = Number(row.dataset.bundleVariantId || "");
    const selectedVariant =
      variants.find((variant) => Number(variant?.id) === previousVariantId && variant?.available) ||
      null;
    const selectedVariantId = selectedVariant ? Number(selectedVariant.id || "") : null;

    row.dataset.bundleVariantId = selectedVariantId ? String(selectedVariantId) : "";

    const sizeInputName = `bundle-size-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const sizeOptionsHtml = variants
      .map((variant, variantIndex) => {
        const variantId = Number(variant?.id || "");
        const isAvailable = Boolean(variant?.available);
        const isActive = selectedVariantId && variantId === selectedVariantId;
        const title = String(variant?.title || "").trim() || "Default";
        const escapedTitle = ThemeProductBundleBuilderUtils.escapeHtml(title);
        const inputId = `bundle-size-${sizeInputName}-${variantIndex}`;
        return `
          <div class="Selectbox textCapitalize${isAvailable ? "" : " is-disabled"}">
            <input
              type="radio"
              id="${inputId}"
              class="inputSelectboxCheckbox"
              name="${sizeInputName}"
              value="${escapedTitle}"
              data-action="bundle-select-size"
              data-bundle-variant-id="${variantId}"
              ${isActive ? "checked" : ""}
              ${isAvailable ? "" : "disabled"}
            >
            <label for="${inputId}" class="SelectboxLabelNormal u-p3" ${isAvailable ? "" : 'aria-disabled="true"'}>
              ${escapedTitle}
            </label>
          </div>
        `;
      })
      .join("");

    host.innerHTML = `
      <div class="VariantSelect mb-l">
        <div class="SelectBox--Wrapper">
          ${sizeOptionsHtml}
        </div>
      </div>
    `;
    ThemeProductBundleBuilderUtils.updateBundleAddToCartState(context);
  }

  static getBundleSelectionState(context) {
    const root = context.getBundleRoot();
    const rows = root.querySelectorAll(".pdp-bundle-builder__row");
    if (!rows.length) {
      return {
        hasMissingSelection: false,
        hasUnavailableBundle: false,
      };
    }

    return Array.from(rows).reduce((state, row) => {
      const activeButton = row.querySelector('.pdp-connected-grid__cell.is-active [data-action="bundle-select-option"]');
      if (!activeButton) {
        state.hasMissingSelection = true;
        return state;
      }

      const variants = ThemeProductBundleAddPayloadBuilder.parseVariants(activeButton);
      if (!Array.isArray(variants) || !variants.length) {
        state.hasUnavailableBundle = true;
        return state;
      }

      const selectedVariantId = Number(row.dataset.bundleVariantId || "");
      const selectedVariant = variants.find((variant) => Number(variant?.id) === selectedVariantId);
      if (!selectedVariantId || !selectedVariant) {
        state.hasMissingSelection = true;
        return state;
      }

      if (!selectedVariant.available) {
        state.hasUnavailableBundle = true;
      }

      return state;
    }, {
      hasMissingSelection: false,
      hasUnavailableBundle: false,
    });
  }

  static hasUnavailableBundleSelection(context) {
    const state = ThemeProductBundleBuilderUtils.getBundleSelectionState(context);
    return state.hasMissingSelection || state.hasUnavailableBundle;
  }

  static updateBundleAddToCartState(context) {
    const root = context.getBundleRoot();
    if (!root.querySelector(".pdp-bundle-builder")) return;

    const variantSelect = root.querySelector("variant-selects");
    const mainVariantAvailable = !!variantSelect?.currentVariant?.available;
    const bundleSelectionState = ThemeProductBundleBuilderUtils.getBundleSelectionState(context);
    const shouldDisableAtc =
      bundleSelectionState.hasMissingSelection ||
      bundleSelectionState.hasUnavailableBundle ||
      !mainVariantAvailable;
    const buttonState =
      !mainVariantAvailable || bundleSelectionState.hasUnavailableBundle
        ? "soldOut"
        : "addToCart";
    const productComponent =
      root?.matches?.("product-component") ? root : root.closest?.("product-component");
    const buttons = new Set(root.querySelectorAll(".product__atc-button"));
    productComponent?.stickyAtcWrapper
      ?.querySelectorAll?.(".product__atc-button")
      ?.forEach((button) => buttons.add(button));

    buttons.forEach((button) => {
      if (productComponent?.syncBundleAtcButtonState) {
        productComponent.syncBundleAtcButtonState(button, shouldDisableAtc, buttonState);
        return;
      }

      if (shouldDisableAtc) {
        button.setAttribute("disabled", "disabled");
        button.setAttribute("aria-disabled", "true");
        return;
      }

      button.removeAttribute("disabled");
      button.removeAttribute("aria-disabled");
    });

    productComponent?.updatePriceButtonATC?.();
  }
}

window.ThemeProduct.BundleAddPayloadBuilder = ThemeProductBundleAddPayloadBuilder;
window.ThemeProduct.BundleBuilderUtils = ThemeProductBundleBuilderUtils;
